import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  ContentPriorityType,
  DeleteDecryptKeyAction,
  EditIncognitoEnabledAction,
  MainState,
  SetAccountsAction,
  SetAnimesAction,
  SetBalanceAction,
  SetCanBroadcastMessagesAction,
  SetCanUseSponsorSubscriptionAction,
  SetCanWithdrawAction,
  SetCard,
  SetContentPriorityAction,
  SetCreateMenuShownAction,
  SetDecryptKeyAction,
  SetDecryptKeyActionLegacy,
  SetDecryptKeysAction,
  SetErrorAction,
  SetEurBalanceAction,
  SetEurTopUpFeeAction,
  SetFeatureAsShownAction,
  SetFeePercentAction,
  SetPriceLimitsAction,
  SetFraudAcceptedAction,
  SetGenderFilterAction,
  SetGroupAction,
  SetGroupsAction,
  SetHasBotAction,
  SetHasNotifyAction,
  SetHasPasswordAction,
  SetHasPersonalSubscriptionAction,
  SetHasSubscriptionAction,
  SetIncognitoEnabledAction,
  SetIsWatermarkDisabledAction,
  SetMainPageDataAction,
  SetManagerLinkAction,
  SetMVerifiedAction,
  SetNewDialogsAction,
  SetNotifyEnabledAction,
  SetOfflineAction,
  SetPermissionsAction,
  SetPlayingVideoAction,
  SetPopupAction,
  SetShowFeaturesAction,
  SetShowInstallPromptAction,
  SetSubscriptionPriceAction,
  SetSubscriptionsPricesAction,
  SetTonAddressAction,
  SetTopUpAddressAction,
  SetUserAction,
  SetUsersAction,
  SetUsersArrayAction,
} from './interface';
import { RootState } from 'store/rootReducer';
import { GenderFilterEnum, UserModel } from '$store/models';
import { t } from '$translation/helper';
import { canInstallPWA, getStorageItem, isDesktop, isSafari } from '$utils';

const initialState: MainState = {
  users: {},
  groups: {},
  anime: {},
  accounts: [],
  newDialogs: 0,
  hasNotify: false,
  contentPriority: ContentPriorityType.NONE,
  genderFilter: GenderFilterEnum.NONE,
  isNotifyEnabled: window.Notification && Notification.permission === 'granted',
  playingVideo: '',
  isMainPageLoading: true,
  mainPagePosts: [],
  mainPageGroups: [],
  permissions: {
    report: false,
    finance: false,
    users: false,
    ads: false,
    utm: false,
    messages: false,
    translation: false,
    battle: false,
    migrateAccount: false,
    topUpManual: false,
    topUpWelcomeBonus: false,
    withdrawal: false,
    changePassword: false,
    auditLog: false,
    apiTokens: false,
    moderationAdmin: false,
  },
  popup: null,
  hasSubscription: false,
  hasPassword: false,
  hasBot: false,
  topUpAddress: '',
  subscriptionPrice: 0,
  subscriptionRubPrice: 0,
  hasPersonalSubscription: false,
  decryptKeyLegacy: '', // TODO: remove property
  decryptKeys: {},
  balance: 0,
  eurBalance: 0,
  placeBalance: 0,
  canWithdraw: true,
  mVerified: false,
  adultPrice: { ton: 5, eur: 10 },
  otherPrice: { ton: 2, eur: 4 },
  isWatermarkDisabled: false,
  canUseSponsorSubscription: false,
  tonAddress: undefined,
  card: null,
  canBroadcastMessages: false,
  showInstallPrompt:
    getStorageItem &&
    getStorageItem('skipPwaBanner') !== 'true' &&
    !(isSafari() && isDesktop()) &&
    !canInstallPWA(),
  eurTopUpFeeAction: 0,
  feePercent: 0.1,
  priceLimits: {},
  offline: false,
  incognito: false,
  error: null,
  showFeatures: {},
  isFraudAccepted: false,
  managerLink: '',
  isCreateMenuShown: false,
  agePopoverHidden: getStorageItem('agePopoverHidden') === 'true',
  countryCode: getStorageItem('countryCode') ?? '',
  payInfo: {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  },
  isModer: false,
  isCryptoEnabled: false,
  isBlackFriday2025Enabled: false,
  shouldBindEmail: false,
};

const userTransformer = (user: UserModel) => {
  if (user.isBanned) {
    const result = { ...user };
    result.firstName = t('banned_first_name');
    return result;
  } else {
    return user;
  }
};

export const { actions, reducer } = createSlice({
  name: 'main',
  initialState,
  reducers: {
    setCryptoEnabled(state, action: PayloadAction<boolean>) {
      state.isCryptoEnabled = action.payload;
    },
    setBlackFriday2025Enabled(state, action: PayloadAction<boolean>) {
      state.isBlackFriday2025Enabled = action.payload;
    },
    setPayInfo(state, action: PayloadAction<typeof initialState.payInfo>) {
      state.payInfo = action.payload;
    },
    setCountryCode(state, action: PayloadAction<string>) {
      state.countryCode = action.payload;
      window.countryCode = action.payload;
    },
    setAgePopoverHidden(state) {
      state.agePopoverHidden = true;
    },
    setUsers(state, action: SetUsersAction) {
      for (let userId in action.payload) {
        let user = action.payload[userId];
        state.users[user.id] = userTransformer(user);
      }
    },
    setUsersArray(state, action: SetUsersArrayAction) {
      for (let user of action.payload) {
        state.users[user.id] = userTransformer(user);
      }
    },
    setUser(state, action: SetUserAction) {
      const user = action.payload;
      if (!user?.id) {
        return;
      }
      state.users[user.id] = {
        ...(state.users[user.id] || {}),
        ...userTransformer(user as UserModel),
      };
    },
    setCard(state, action: SetCard) {
      state.card = action.payload;
    },
    removeCard(state) {
      state.card = null;
    },
    setCanBroadcastMessages(state, action: SetCanBroadcastMessagesAction) {
      state.canBroadcastMessages = action.payload;
    },
    setAccounts(state, action: SetAccountsAction) {
      state.accounts = action.payload;
    },
    setGroups(state, action: SetGroupsAction) {
      for (let groupId in action.payload) {
        let group = action.payload[groupId];
        state.groups[group.id] = group;
      }
    },
    setGroup(state, action: SetGroupAction) {
      const groupId = action.payload.id!;
      state.groups[groupId] = {
        ...(state.groups[groupId] || {}),
        ...action.payload,
      };
    },
    setPermissions(state, action: SetPermissionsAction) {
      state.permissions = {
        ...state.permissions,
        ...action.payload,
      };
    },
    setNewDialogs(state, action: SetNewDialogsAction) {
      state.newDialogs = action.payload;
    },
    setHasNotify(state, action: SetHasNotifyAction) {
      state.hasNotify = action.payload;
    },
    setNotifyEnabled(state, action: SetNotifyEnabledAction) {
      state.isNotifyEnabled = action.payload;
    },
    setPlayingVideo(state, action: SetPlayingVideoAction) {
      state.playingVideo = action.payload;
    },
    setContentPriority(state, action: SetContentPriorityAction) {
      state.contentPriority = action.payload;
    },
    setGenderFilter(state, action: SetGenderFilterAction) {
      state.genderFilter = action.payload;
    },
    loadMainPageData() {},
    setMainPageData(state, action: SetMainPageDataAction) {
      state.isMainPageLoading = false;
      state.mainPageGroups = action.payload.groups;
      state.mainPagePosts = action.payload.posts;
    },
    setAnimes(state, action: SetAnimesAction) {
      for (let animeId in action.payload) {
        let anime = action.payload[animeId];
        state.anime[anime.id] = anime;
      }
    },
    setPopup(state, action: SetPopupAction) {
      state.popup = action.payload;
    },
    setHasSubscription(state, action: SetHasSubscriptionAction) {
      state.hasSubscription = action.payload;
    },
    setHasPassword(state, action: SetHasPasswordAction) {
      state.hasPassword = action.payload;
    },
    setCanWithdraw(state, action: SetCanWithdrawAction) {
      state.canWithdraw = action.payload;
    },
    setTopUpAddress(state, action: SetTopUpAddressAction) {
      state.topUpAddress = action.payload;
    },
    setTonAddress(state, action: SetTonAddressAction) {
      state.tonAddress = action.payload;
    },
    setHasPersonalSubscription(state, action: SetHasPersonalSubscriptionAction) {
      state.hasPersonalSubscription = action.payload;
    },
    setSubscriptionPrice(state, action: SetSubscriptionPriceAction) {
      const { ton, rubles } = action.payload;

      state.subscriptionPrice = ton;
      state.subscriptionRubPrice = rubles;
    },
    setDecryptKeyLegacy(state, action: SetDecryptKeyActionLegacy) {
      state.decryptKeyLegacy = action.payload;
    },
    setDecryptKeys(state, action: SetDecryptKeysAction) {
      state.decryptKeys = action.payload;
    },
    setDecryptKey(state, action: SetDecryptKeyAction) {
      const { id, decryptKey } = action.payload;
      state.decryptKeys[id] = decryptKey;
    },
    deleteDecryptKey(state, action: DeleteDecryptKeyAction) {
      delete state.decryptKeys[action.payload];
    },
    setBalance(state, action: SetBalanceAction) {
      state.balance = action.payload;
    },
    setEurBalance(state, action: SetEurBalanceAction) {
      state.eurBalance = action.payload;
    },
    setPlaceBalance(state, action: SetEurBalanceAction) {
      state.placeBalance = action.payload;
    },
    setMVerified(state, action: SetMVerifiedAction) {
      state.mVerified = action.payload;
    },
    setHasBot(state, action: SetHasBotAction) {
      state.hasBot = action.payload;
    },
    setSubscriptionsPrices(state, action: SetSubscriptionsPricesAction) {
      const { adult, other } = action.payload;
      state.adultPrice = adult;
      state.otherPrice = other;
    },
    setIsWatermarkDisabled(state, action: SetIsWatermarkDisabledAction) {
      state.isWatermarkDisabled = action.payload;
    },
    setCanUseSponsorSubscription(state, action: SetCanUseSponsorSubscriptionAction) {
      state.canUseSponsorSubscription = action.payload;
    },
    setShowInstallPrompt(state, action: SetShowInstallPromptAction) {
      state.showInstallPrompt = action.payload;
    },
    setEurTopUpFee(state, action: SetEurTopUpFeeAction) {
      state.eurTopUpFeeAction = action.payload;
    },
    setFeePercent(state, action: SetFeePercentAction) {
      state.feePercent = action.payload;
    },
    setPriceLimits(state, action: SetPriceLimitsAction) {
      state.priceLimits = action.payload;
    },
    setOffline(state, action: SetOfflineAction) {
      state.offline = action.payload;
    },
    setIncognitoEnabled(state, action: SetIncognitoEnabledAction) {
      state.incognito = action.payload;
    },
    editIncognitoEnabled(_, action: EditIncognitoEnabledAction) {},
    setError(state, action: SetErrorAction) {
      state.error = action.payload;
    },
    checkConnection() {},
    setShowFeatures(state, action: SetShowFeaturesAction) {
      state.showFeatures = action.payload;
    },
    setFeatureAsShown(_, action: SetFeatureAsShownAction) {},
    setFraudAccepted(state, action: SetFraudAcceptedAction) {
      state.isFraudAccepted = action.payload;
    },
    setManagerLink(state, action: SetManagerLinkAction) {
      state.managerLink = action.payload;
    },
    setCreateMenuShown(state, action: SetCreateMenuShownAction) {
      state.isCreateMenuShown = action.payload;
    },
    setIsModer(state, action: PayloadAction<boolean>) {
      state.isModer = action.payload;
    },
    setShouldBindEmail(state, action: PayloadAction<boolean>) {
      state.shouldBindEmail = action.payload;
    },
  },
});

export { reducer as mainReducer, actions as mainActions };

export const mainSelector = (state: RootState) => ({
  ...state.main,
  countryCode: getStorageItem('countryCode') ?? state.main.countryCode,
});
export const mainHasBotSelector = (state: RootState) => state.main.hasBot;
export const permissionsSelector = (state: RootState) => state.main.permissions;

export const selectMain = (state: RootState): ReturnType<typeof reducer> => state.main;

export const userSelector = createSelector(
  selectMain,
  (_, userId: number) => userId,
  (main, userId) => {
    return main.users[userId];
  },
);

export const contentPrioritySelector = (state: RootState) => state.main.contentPriority;
export const genderFilterSelector = (state: RootState) => state.main.genderFilter;

export const currentUserSelector = (state: RootState) => {
  return state.auth.currentUserId ? state.main.users[state.auth.currentUserId] : {};
};

export const animeInfoSelector = createSelector(
  selectMain,
  (_, animeId: number) => animeId,
  (main, animeId) => {
    return main.anime[animeId];
  },
);

export const mainFraudAccepted = createSelector(selectMain, (main) => {
  return main.isFraudAccepted;
});

export const agePopoverHiddenSelector = createSelector(selectMain, (main) => {
  return main.agePopoverHidden;
});

export const isCreateMenuShownSelector = createSelector(selectMain, (main) => {
  return main.isCreateMenuShown;
});

export const managerLinkSelector = createSelector(selectMain, (main) => {
  return main.managerLink;
});

export const shouldBindEmailSelector = createSelector(selectMain, (main) => {
  return main.shouldBindEmail;
});
